// Geometry in label millimetres: unit conversion, hit-testing, resize handles,
// clamping and nudging. Pure — SSR/Node safe, no canvas.
//
// Two coordinate systems exist in the editor: CSS px on screen (affected by
// the zoom/pan transform) and label mm (the document). The component converts
// pointer positions to mm first; everything here then works in mm. A grab
// tolerance is a fixed PHYSICAL size (a fingertip), so it is specified in
// screen px and converted with `screenPxToMm` at the current zoom — otherwise
// grabbing would get harder the further you zoom in.

import { getOrnament } from "./ornaments";
import { LIMITS, type CanvasData, type LabelElement, type TextElement } from "./schema";

export interface Point {
  x: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type LabelSize = CanvasData["label"];

export type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

export const ALL_HANDLES: readonly Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const CORNERS: readonly Handle[] = ["nw", "ne", "se", "sw"];

/** Millimetres per inch. */
export const MM_PER_INCH = 25.4;

export const mmToPx = (mm: number, pxPerMm: number) => mm * pxPerMm;
export const pxToMm = (px: number, pxPerMm: number) => (pxPerMm > 0 ? px / pxPerMm : 0);
export const dpiToPxPerMm = (dpi: number) => dpi / MM_PER_INCH;

/**
 * Convert an on-screen distance to mm. `cssPxPerMm` is the fitted scale of the
 * label and `zoom` the extra CSS transform scale on top of it.
 */
export function screenPxToMm(screenPx: number, cssPxPerMm: number, zoom = 1): number {
  const k = cssPxPerMm * zoom;
  return k > 0 ? screenPx / k : 0;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round2 = (v: number) => Math.round(v * 100) / 100;

export function boxOf(el: Pick<LabelElement, "x" | "y" | "width" | "height">): Box {
  return { x: el.x, y: el.y, width: el.width, height: el.height };
}

export function inflate(b: Box, d: number): Box {
  return { x: b.x - d, y: b.y - d, width: b.width + 2 * d, height: b.height + 2 * d };
}

export function containsPoint(b: Box, p: Point): boolean {
  return p.x >= b.x && p.x <= b.x + b.width && p.y >= b.y && p.y <= b.y + b.height;
}

/** Union of boxes (null for none). */
export function unionBox(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of boxes) {
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width);
    y1 = Math.max(y1, b.y + b.height);
  }
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

// ---- Hit-testing ------------------------------------------------------------

/**
 * Does point `p` (mm) hit element `el` with tolerance `tolMm`?
 * - Text, images, filled shapes: anywhere inside the (inflated) box / oval.
 * - Unfilled rectangle and oval frames: only near the border, so text sitting
 *   inside a frame stays clickable even when the frame is above it.
 * - Dividers: the inflated line box.
 * - Ornaments: their box. Open-centred ones (frames, corner sets, rings,
 *   wreaths, shields — `hollow` in ornaments.ts) leave a centre box inset by
 *   hollow × min(width, height) click-through, for the same reason.
 */
export function hitElement(el: LabelElement, p: Point, tolMm: number): boolean {
  const outer = inflate(boxOf(el), tolMm);
  if (!containsPoint(outer, p)) return false;
  switch (el.type) {
    case "text":
    case "image":
    case "line":
      return true;
    case "rect": {
      if (el.fill) return true;
      const band = el.stroke_mm + tolMm;
      const inner: Box = {
        x: el.x + band,
        y: el.y + band,
        width: el.width - 2 * band,
        height: el.height - 2 * band,
      };
      if (inner.width <= 0 || inner.height <= 0) return true;
      return !containsPoint(inner, p);
    }
    case "ellipse": {
      const rx = el.width / 2;
      const ry = el.height / 2;
      if (rx <= 0 || ry <= 0) return true;
      const cx = el.x + rx;
      const cy = el.y + ry;
      const dx = p.x - cx;
      const dy = p.y - cy;
      // Normalised radius: 1 on the oval's outer edge.
      const d = Math.sqrt((dx / rx) ** 2 + (dy / ry) ** 2);
      if (el.fill) return d <= 1 || distanceToEllipseEdge(dx, dy, d) <= tolMm;
      if (d === 0) return Math.min(rx, ry) <= el.stroke_mm + tolMm;
      // The stroke is drawn inside the box, centred stroke_mm/2 in from the edge.
      const dist = distanceToEllipseEdge(dx, dy, d);
      const inside = d < 1;
      return inside ? dist <= el.stroke_mm + tolMm : dist <= tolMm;
    }
    case "ornament": {
      const hollow = getOrnament(el.asset)?.hollow ?? 0;
      if (hollow <= 0) return true;
      const d = hollow * Math.min(el.width, el.height) + tolMm;
      const inner: Box = { x: el.x + d, y: el.y + d, width: el.width - 2 * d, height: el.height - 2 * d };
      if (inner.width <= 0 || inner.height <= 0) return true;
      return !containsPoint(inner, p);
    }
  }
}

/** Radial approximation of the distance from a point to the oval's outer edge. */
function distanceToEllipseEdge(dx: number, dy: number, d: number): number {
  const r = Math.hypot(dx, dy);
  if (d === 0) return Infinity;
  return Math.abs(r - r / d);
}

/**
 * The element under `p`, topmost first (array order = z-order, last on top).
 * `preferId` (the current selection) wins when it is hit too, so a selected
 * element can be dragged even where another element overlaps it.
 */
export function hitTest(
  doc: Pick<CanvasData, "elements">,
  p: Point,
  tolMm: number,
  preferId?: string | null,
): LabelElement | null {
  if (preferId) {
    const pref = doc.elements.find((e) => e.id === preferId);
    if (pref && hitElement(pref, p, tolMm)) return pref;
  }
  for (let i = doc.elements.length - 1; i >= 0; i--) {
    if (hitElement(doc.elements[i], p, tolMm)) return doc.elements[i];
  }
  return null;
}

// ---- Handles ----------------------------------------------------------------

/** Which resize handles an element offers. */
export function handlesFor(el: LabelElement): readonly Handle[] {
  switch (el.type) {
    case "text":
      // Height follows the wrapped text: sides change the wrap width, corners scale the type.
      return ["nw", "ne", "se", "sw", "e", "w"];
    case "image":
    case "ornament":
      // Images and ornaments always keep their aspect ratio → corners only.
      return CORNERS;
    case "line":
      return ["e", "w"];
    default:
      return ALL_HANDLES;
  }
}

export function handlePoint(b: Box, h: Handle): Point {
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  const r = b.x + b.width;
  const btm = b.y + b.height;
  switch (h) {
    case "nw": return { x: b.x, y: b.y };
    case "n": return { x: cx, y: b.y };
    case "ne": return { x: r, y: b.y };
    case "e": return { x: r, y: cy };
    case "se": return { x: r, y: btm };
    case "s": return { x: cx, y: btm };
    case "sw": return { x: b.x, y: btm };
    case "w": return { x: b.x, y: cy };
  }
}

export function handlePoints(el: LabelElement): { handle: Handle; point: Point }[] {
  const b = boxOf(el);
  return handlesFor(el).map((handle) => ({ handle, point: handlePoint(b, handle) }));
}

/** The handle of `el` within `radiusMm` of `p` (nearest wins), or null. */
export function hitHandle(el: LabelElement, p: Point, radiusMm: number): Handle | null {
  let best: Handle | null = null;
  let bestD = Infinity;
  for (const { handle, point } of handlePoints(el)) {
    const d = Math.hypot(p.x - point.x, p.y - point.y);
    if (d <= radiusMm && d < bestD) {
      best = handle;
      bestD = d;
    }
  }
  return best;
}

/** CSS cursor for a handle. */
export function handleCursor(h: Handle): string {
  return h === "n" || h === "s"
    ? "ns-resize"
    : h === "e" || h === "w"
      ? "ew-resize"
      : h === "nw" || h === "se"
        ? "nwse-resize"
        : "nesw-resize";
}

// ---- Clamping, moving, nudging ---------------------------------------------

/** Keep an element fully inside the label (it is shrunk first if it is larger). */
export function clampToLabel<T extends LabelElement>(el: T, label: LabelSize): T {
  const width = Math.min(el.width, label.width_mm);
  const height = Math.min(el.height, label.height_mm);
  const x = clamp(el.x, 0, label.width_mm - width);
  const y = clamp(el.y, 0, label.height_mm - height);
  if (x === el.x && y === el.y && width === el.width && height === el.height) return el;
  return { ...el, x: round2(x), y: round2(y), width: round2(width), height: round2(height) };
}

/** `start` translated by (dx, dy) mm, clamped inside the label. Position only — never resizes. */
export function moveElement<T extends LabelElement>(
  start: T,
  dx: number,
  dy: number,
  label: LabelSize,
): T {
  const x = clamp(start.x + dx, 0, Math.max(0, label.width_mm - start.width));
  const y = clamp(start.y + dy, 0, Math.max(0, label.height_mm - start.height));
  return { ...start, x: round2(x), y: round2(y) };
}

/** Keyboard nudge: 1 mm, or 5 mm with Shift (the caller picks `step`). */
export function nudge<T extends LabelElement>(
  el: T,
  dir: "left" | "right" | "up" | "down",
  step: number,
  label: LabelSize,
): T {
  const dx = dir === "left" ? -step : dir === "right" ? step : 0;
  const dy = dir === "up" ? -step : dir === "down" ? step : 0;
  return moveElement(el, dx, dy, label);
}

// ---- Resizing ---------------------------------------------------------------

export interface ResizeOptions {
  /** Shift held: corners keep the aspect ratio (rectangles and ovals). */
  keepAspect?: boolean;
  /** Smallest box, mm (defaults to LIMITS.minSizeMm). */
  minMm?: number;
}

/**
 * Resize `start` by dragging `handle` by (dx, dy) mm from where it was grabbed.
 * Using the DELTA (not the absolute pointer) means grabbing a handle a few px
 * off-centre never makes the box jump.
 * - The opposite edge / corner stays anchored.
 * - The result stays inside the label and no smaller than `minMm`.
 * - Images and ornaments always keep their aspect; rectangles and ovals do on
 *   corners with Shift; text corners scale `size_mm` with the width (height is
 *   re-measured by the caller), text sides change the wrap width only;
 *   dividers change width only. An ornament's minimum applies to its LONG
 *   side, so a slim divider ornament can still be made small.
 */
export function resizeElement<T extends LabelElement>(
  start: T,
  handle: Handle,
  dx: number,
  dy: number,
  label: LabelSize,
  opts: ResizeOptions = {},
): T {
  const min = opts.minMm ?? LIMITS.minSizeMm;
  const W = label.width_mm;
  const H = label.height_mm;
  const hasW = handle.includes("w");
  const hasE = handle.includes("e");
  const hasN = handle.includes("n");
  const hasS = handle.includes("s");
  const isCorner = (hasW || hasE) && (hasN || hasS);

  const left0 = start.x;
  const top0 = start.y;
  const right0 = start.x + start.width;
  const bottom0 = start.y + start.height;

  const lockAspect =
    isCorner &&
    (start.type === "image" || start.type === "ornament" || start.type === "text" || !!opts.keepAspect) &&
    start.width > 0 &&
    start.height > 0;

  // Text and dividers only resize horizontally from their side handles.
  const verticalAllowed = start.type !== "line" && !(start.type === "text" && !isCorner);

  if (lockAspect) {
    const w0 = start.width;
    const h0 = start.height;
    const rawW = hasE ? w0 + dx : w0 - dx;
    const rawH = hasS ? h0 + dy : h0 - dy;
    let s = Math.max(rawW / w0, rawH / h0);
    // Room available from the anchored corner to the label edges.
    const availW = hasE ? W - left0 : right0;
    const availH = hasS ? H - top0 : bottom0;
    const sMax = Math.min(availW / w0, availH / h0);
    const sMin = start.type === "ornament" ? min / Math.max(w0, h0) : Math.max(min / w0, min / h0);
    s = clamp(s, Math.min(sMin, sMax), sMax);
    const width = w0 * s;
    const height = h0 * s;
    const x = hasE ? left0 : right0 - width;
    const y = hasS ? top0 : bottom0 - height;
    const out = {
      ...start,
      x: round2(x),
      y: round2(y),
      width: round2(width),
      height: round2(height),
    } as T;
    if (start.type === "text") {
      const t = start as TextElement;
      (out as TextElement).size_mm = round2(
        clamp(t.size_mm * s, LIMITS.textSizeMm.min, LIMITS.textSizeMm.max),
      );
    }
    return out;
  }

  let left = left0;
  let right = right0;
  let top = top0;
  let bottom = bottom0;
  if (hasW) left = clamp(left0 + dx, 0, right0 - min);
  if (hasE) right = clamp(right0 + dx, left0 + min, W);
  if (verticalAllowed) {
    if (hasN) top = clamp(top0 + dy, 0, bottom0 - min);
    if (hasS) bottom = clamp(bottom0 + dy, top0 + min, H);
  }
  return {
    ...start,
    x: round2(left),
    y: round2(top),
    width: round2(right - left),
    height: round2(bottom - top),
  } as T;
}
